import { useState, useEffect } from 'react';
import { Bell, Calendar, DollarSign, Filter, Clock, Eye, Paperclip, Download, CheckCircle2, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import apiClient from '@/utils/apiClient';
import downloadFile from '@/utils/fileDownloader';
import { toast } from 'sonner';
import RichTextViewer, { stripHtml } from '@/components/shared/RichTextViewer';

const getAttachmentUrl = (rawUrl) => {
  if (!rawUrl) return '';
  if (typeof rawUrl === 'object' && rawUrl.url) return getAttachmentUrl(rawUrl.url);
  if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) return rawUrl;
  const backendBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  return `${backendBase}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
};

const getDownloadUrl = (rawUrl) => {
  const url = getAttachmentUrl(rawUrl);
  if (url.includes('/api/v1/common/attachments/')) {
    return url.includes('?') ? `${url}&download=true` : `${url}?download=true`;
  }
  return url;
};

const getCleanFilename = (rawUrl, fallbackIndex = 0) => {
  if (!rawUrl) return `Attachment ${fallbackIndex + 1}`;
  if (typeof rawUrl === 'object' && rawUrl.name) return rawUrl.name;
  try {
    const rawName = decodeURIComponent(String(rawUrl).split('?')[0].split('/').pop() || '');
    const parts = rawName.split('_');
    if (parts.length > 1 && parts[0].length === 12) {
      return parts.slice(1).join('_');
    }
    return rawName || `Attachment ${fallbackIndex + 1}`;
  } catch (e) {
    return `Attachment ${fallbackIndex + 1}`;
  }
};

const ParentNoticeBoard = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    try {
      const response = await apiClient.get('/v1/parent/announcements/');
      setNotices(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to load notices:', error);
      toast.error('Failed to load notices');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (noticeId) => {
    try {
      await apiClient.post(`/v1/parent/announcements/${noticeId}/mark-read`);
      // Update local state to show it's read
      setNotices(prev => prev.map(n =>
        n.id === noticeId ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleViewNotice = (notice) => {
    setSelectedNotice(notice);
    setShowDetailModal(true);
    if (!notice.is_read) {
      markAsRead(notice.id);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryIcon = (category) => {
    const icons = {
      financial: <DollarSign className="w-5 h-5 text-green-600" />,
      general: <Bell className="w-5 h-5 text-blue-600" />,
      holidays: <Calendar className="w-5 h-5 text-purple-600" />
    };
    return icons[category] || <Bell className="w-5 h-5 text-gray-600" />;
  };

  const getCategoryBadge = (category) => {
    const badges = {
      financial: 'bg-green-100 text-green-800',
      general: 'bg-blue-100 text-blue-800',
      holidays: 'bg-purple-100 text-purple-800'
    };
    return badges[category] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'border-red-300 bg-red-50/50',
      medium: 'border-orange-300 bg-orange-50/50',
      low: 'border-blue-300 bg-blue-50/50'
    };
    return colors[priority] || 'border-gray-200';
  };

  const filteredNotices = categoryFilter === 'all'
    ? notices
    : notices.filter(n => (n.category || n.type) === categoryFilter);

  const categoryCounts = {
    all: notices.length,
    financial: notices.filter(n => (n.category || n.type) === 'financial').length,
    general: notices.filter(n => (n.category || n.type) === 'general').length,
    holidays: notices.filter(n => (n.category || n.type) === 'holidays').length
  };

  if (loading) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-gray-500 animate-pulse">Loading important notices...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ color: '#2C5F7F' }}>Notice Board</h1>
          <p className="text-gray-500 mt-1 text-sm">Stay updated with the latest announcements from the hostel.</p>
        </div>
        <div className="bg-blue-50 px-4 py-2 rounded-full border border-blue-100">
          <span className="text-blue-700 font-semibold text-sm">
            {notices.filter(n => !n.is_read).length} New Announcements
          </span>
        </div>
      </div>

      {/* Category Filters */}
      <div className="flex gap-2 flex-wrap bg-white p-2 rounded-xl shadow-sm border">
        {['all', 'general', 'financial', 'holidays'].map((cat) => (
          <Button
            key={cat}
            variant={categoryFilter === cat ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setCategoryFilter(cat)}
            className={`rounded-lg capitalize ${categoryFilter === cat ? 'bg-[#2C5F7F] text-white hover:bg-[#234d67]' : 'text-gray-600'
              }`}
          >
            {cat} ({categoryCounts[cat]})
          </Button>
        ))}
      </div>

      {/* Notices List */}
      <div className="grid gap-4">
        {filteredNotices.length === 0 ? (
          <Card className="border-dashed border-2">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bell className="w-8 h-8 text-gray-300" />
              </div>
              <p className="text-gray-500 font-medium">All caught up! No notices in this category.</p>
            </CardContent>
          </Card>
        ) : (
          filteredNotices.map((notice) => {
            const category = notice.category || notice.type || 'general';
            const priority = notice.priority || 'medium';
            const isUnread = !notice.is_read;

            return (
              <Card
                key={notice.id}
                className={`group cursor-pointer transition-all hover:shadow-md border-l-4 ${getPriorityColor(priority)} ${isUnread ? 'bg-white ring-1 ring-blue-100' : 'bg-gray-50/50'
                  }`}
                onClick={() => handleViewNotice(notice)}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className={`mt-1 p-2 rounded-lg ${isUnread ? 'bg-blue-50' : 'bg-gray-100'}`}>
                      {getCategoryIcon(category)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`px-2.5 py-0.5 text-[10px] rounded-full font-bold uppercase tracking-wider ${getCategoryBadge(category)}`}>
                          {category}
                        </span>
                        {priority === 'high' && (
                          <span className="px-2.5 py-0.5 text-[10px] rounded-full font-bold bg-red-100 text-red-700 uppercase tracking-wider">
                            Urgent
                          </span>
                        )}
                        {isUnread && (
                          <span className="flex items-center gap-1 px-2.5 py-0.5 text-[10px] rounded-full font-bold bg-blue-600 text-white uppercase tracking-wider">
                            <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                            New
                          </span>
                        )}
                      </div>

                      <h3 className={`font-bold text-lg mb-1 truncate ${isUnread ? 'text-[#2C5F7F]' : 'text-gray-700'}`}>
                        {notice.title || notice.name}
                      </h3>

                      <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                        {stripHtml(notice.message || notice.description)}
                      </p>

                      <div className="flex items-center gap-4 text-xs font-medium text-gray-500">
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" />
                          {formatDate(notice.created_at || notice.start_date)}
                        </span>
                        {notice.attachments?.length > 0 && (
                          <span className="flex items-center gap-1.5 text-blue-600">
                            <Paperclip className="w-3.5 h-3.5" />
                            {notice.attachments.length} Attachments
                          </span>
                        )}
                        {notice.is_read && (
                          <span className="flex items-center gap-1.5 text-green-600">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Read
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="self-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon">
                        <Eye className="w-5 h-5 text-[#2C5F7F]" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Notice Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedNotice && (
            <div className="space-y-6 py-2">
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2.5 py-0.5 text-[10px] rounded-full font-bold uppercase tracking-wider ${getCategoryBadge(selectedNotice.category || selectedNotice.type)}`}>
                    {selectedNotice.category || selectedNotice.type}
                  </span>
                  {selectedNotice.priority === 'high' && (
                    <span className="px-2.5 py-0.5 text-[10px] rounded-full font-bold bg-red-100 text-red-700 uppercase tracking-wider">
                      High Priority
                    </span>
                  )}
                </div>
                <DialogTitle className="text-2xl font-bold text-[#2C5F7F]">
                  {selectedNotice.title || selectedNotice.name}
                </DialogTitle>
                <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                  <Clock className="w-4 h-4" />
                  <span>Sent on {formatDate(selectedNotice.created_at || selectedNotice.start_date)}</span>
                </div>
              </DialogHeader>

              <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                <RichTextViewer content={selectedNotice.message || selectedNotice.description} />
              </div>

              {selectedNotice.attachments?.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-bold text-sm text-gray-700 flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    Attachments
                  </h4>
                  <div className="grid gap-2">
                    {selectedNotice.attachments.map((att, idx) => {
                      const filename = getCleanFilename(att, idx);
                      const viewUrl = getAttachmentUrl(att);
                      const downloadUrl = getDownloadUrl(att);
                      return (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-3 bg-white border rounded-lg hover:border-blue-200 transition-colors"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="p-2 bg-blue-100 text-blue-600 rounded">
                              <Paperclip className="w-4 h-4" />
                            </div>
                            <span className="text-sm font-medium text-gray-800 truncate max-w-[280px]">
                              {filename}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <a
                              href={viewUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                              View
                            </a>
                            <button
                              type="button"
                              onClick={() => downloadFile(downloadUrl, filename)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-[#2C5F7F] text-white hover:bg-[#234d67] transition-colors"
                            >
                              <Download className="w-3.5 h-3.5" />
                              Download
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="pt-4 flex justify-end">
                <Button onClick={() => setShowDetailModal(false)} className="bg-[#2C5F7F] text-white">
                  Close Notice
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParentNoticeBoard;
